import { normalizeSearchText } from '../search-text';
import { TpvBusinessUnit } from './business-unit.model';
import { TpvCompany } from './company-directory.model';
import { TpvOptionHierarchy } from './directory-filter-option.model';
import {
  TpvAccessCompany,
  TpvAccessMerchant,
  TpvAccessUnit,
  TpvUserAccess,
} from './tpv-user-access.model';

/**
 * "Foco" de TPV (feature 152): la empresa o unidad de negocio que el operador
 * eligió en `/products`. Es un filtro de PRESENTACIÓN (acota el selector de
 * comercio y las migas del dashboard); la autoridad sigue siendo del back, que
 * decide por petición.
 */
export type TpvFocusLevel = 'company' | 'unit';

/** Solo nivel + identificador externo: ningún nombre ni otro dato (R21). */
export interface TpvEntityFocus {
  readonly level: TpvFocusLevel;
  readonly externalId: string;
}

/** Tope defensivo del identificador persistido (un UUID mide 36). */
const MAX_EXTERNAL_ID = 128;

/**
 * Valida la forma persistida (R21): un objeto con nivel conocido e id no
 * vacío. Cualquier otra cosa (texto, JSON roto ya parseado, campos de más con
 * nombres) se descarta como "sin foco" — solo se reconstruye lo necesario.
 */
export function parseTpvEntityFocus(raw: unknown): TpvEntityFocus | null {
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) return null;
  const { level, externalId } = raw as { level?: unknown; externalId?: unknown };
  if (level !== 'company' && level !== 'unit') return null;
  if (typeof externalId !== 'string') return null;
  const id = externalId.trim();
  if (!id || id.length > MAX_EXTERNAL_ID) return null;
  return { level, externalId: id };
}

/** El foco resuelto contra el árbol de accesos del dashboard. */
export interface ResolvedTpvFocus {
  readonly company: TpvAccessCompany;
  readonly unit: TpvAccessUnit | null;
  /** Los comercios de la empresa (todas sus unidades) o de la unidad enfocada. */
  readonly merchants: TpvAccessMerchant[];
}

/**
 * Resuelve el foco contra `user/access` (R22–R24). `null` ⇒ el foco no
 * corresponde a ninguna empresa o unidad legible y hay que quitarlo (R23).
 *
 * ⛔ Q5: el id del foco sale del DIRECTORIO (`TpvCompany.idCompany`,
 * `TpvBusinessUnit.idUnit`) y aquí se compara con `external_id_*` del árbol. Si
 * el back no los hace coincidir, esto da `null` y el foco se limpia: falla
 * cerrado, nunca enseña comercios de otra entidad.
 */
export function resolveTpvFocus(access: TpvUserAccess | null, focus: TpvEntityFocus): ResolvedTpvFocus | null {
  for (const company of access?.companies ?? []) {
    if (focus.level === 'company') {
      if (company.externalIdCompany !== focus.externalId) continue;
      return { company, unit: null, merchants: (company.units ?? []).flatMap((u) => u.merchants ?? []) };
    }
    const unit = (company.units ?? []).find((u) => u.externalIdUnit === focus.externalId);
    if (unit) return { company, unit, merchants: unit.merchants ?? [] };
  }
  return null;
}

/** Una fila del panel TPV de `/products` (R46). */
export interface TpvSelectableEntity {
  /** Identidad para `track`: nivel + id externo (los UUID no chocan entre niveles, pero así es explícito). */
  readonly id: string;
  readonly level: TpvFocusLevel;
  readonly externalId: string;
  readonly name: string;
  /** Dato secundario: en una unidad, el nombre de su empresa. */
  readonly secondary: string | null;
}

/**
 * Lista plana para el panel: empresas primero y luego las unidades, cada una
 * con el nombre de su empresa (`companyName` del directorio o, si falta, el de
 * su empresa por `idCompany`; índice `Map` en una pasada, O(n)). Las filas sin
 * id externo se descartan: no se podrían enfocar.
 */
export function tpvSelectableEntities(
  companies: readonly TpvCompany[],
  units: readonly TpvBusinessUnit[],
): TpvSelectableEntity[] {
  const nombrePorEmpresa = new Map<string, string>();
  const filas: TpvSelectableEntity[] = [];

  for (const company of companies ?? []) {
    const id = String(company?.idCompany ?? '').trim();
    if (!id) continue;
    const name = String(company.name || company.legalName || '').trim();
    nombrePorEmpresa.set(id, name);
    filas.push({ id: `company:${id}`, level: 'company', externalId: id, name, secondary: null });
  }

  for (const unit of units ?? []) {
    const id = String(unit?.idUnit ?? '').trim();
    if (!id) continue;
    const empresa = String(unit.companyName ?? '').trim() || nombrePorEmpresa.get(String(unit.idCompany ?? '')) || '';
    filas.push({
      id: `unit:${id}`,
      level: 'unit',
      externalId: id,
      name: String(unit.name ?? '').trim(),
      secondary: empresa || null,
    });
  }

  return filas;
}

/**
 * Filtro EN EL CLIENTE del panel TPV de `/products`: las filas cuyo nombre o
 * dato secundario (en una unidad, el nombre de su empresa) contienen el término,
 * sin distinguir mayúsculas ni acentos. Término vacío ⇒ la MISMA lista. Una
 * pasada, O(n).
 *
 * No sustituye a la búsqueda del servidor: el back busca además por razón social
 * y RFC, que estas filas no traen. Solo cubre los términos demasiado cortos para
 * ir al servidor.
 */
export function filterTpvSelectableEntities(
  list: readonly TpvSelectableEntity[],
  term: string,
): readonly TpvSelectableEntity[] {
  const buscado = normalizeSearchText(term);
  if (!buscado) return list;
  return list.filter((fila) => normalizeSearchText(`${fila.name} ${fila.secondary ?? ''}`).includes(buscado));
}

/**
 * Opción de un catálogo Empresa / Unidad del panel de filtros, vista desde el foco:
 * solo su valor (lo que viaja) y su jerarquía (de dónde salen los UUID). Estructural,
 * para no depender de la capa de presentación.
 */
export interface TpvFocusCatalogOption {
  readonly value: string;
  readonly hierarchy?: TpvOptionHierarchy;
}

/**
 * El foco traducido a los valores del panel de filtros de UNA pantalla (el mismo valor
 * que el operador elegiría a mano: el nombre en Transacciones y Dispersiones, el UUID en
 * Comercios). `unit` es `null` con un foco de empresa.
 */
export interface TpvFocusFilter {
  readonly company: string;
  readonly unit: string | null;
}

/**
 * Valor ÚNICO del catálogo cuyo UUID (`idDe`) es el buscado. Ninguno, o dos opciones con
 * valores distintos para el mismo UUID, ⇒ `null`: no se adivina cuál.
 */
function valorUnicoPorId(
  opciones: readonly TpvFocusCatalogOption[],
  id: string,
  idDe: (o: TpvFocusCatalogOption) => string | undefined,
): TpvFocusCatalogOption | null {
  let hallada: TpvFocusCatalogOption | null = null;
  for (const o of opciones ?? []) {
    if (!o?.value || idDe(o) !== id) continue;
    if (hallada && hallada.value !== o.value) return null;
    hallada = o;
  }
  return hallada;
}

/**
 * Traduce el foco a los filtros Empresa (y Unidad) del panel, resolviéndolo contra los
 * catálogos que la pantalla YA cargó del scope del operador. Falla CERRADO (`null`):
 * - sin foco, o un foco que no está en el catálogo ⇒ no se aplica nada (el foco solo
 *   acota; nunca amplía lo que el back ya devuelve);
 * - en una unidad, también su empresa tiene que estar en el catálogo de empresas por el
 *   MISMO UUID (la cascada del panel llenaría la empresa al elegir la unidad a mano);
 * - se compara solo por UUID: el foco no guarda nombres, y un nombre no identifica.
 * Una pasada por catálogo, O(n).
 */
export function resolveTpvFocusFilter(
  focus: TpvEntityFocus | null,
  companies: readonly TpvFocusCatalogOption[],
  units: readonly TpvFocusCatalogOption[],
): TpvFocusFilter | null {
  if (!focus) return null;
  const idEmpresa = (o: TpvFocusCatalogOption) => o.hierarchy?.company?.id;
  if (focus.level === 'company') {
    const empresa = valorUnicoPorId(companies, focus.externalId, idEmpresa);
    return empresa ? { company: empresa.value, unit: null } : null;
  }
  const unidad = valorUnicoPorId(units, focus.externalId, (o) => o.hierarchy?.unit?.id);
  const idPadre = unidad?.hierarchy?.company?.id;
  if (!unidad || !idPadre) return null;
  const empresa = valorUnicoPorId(companies, idPadre, idEmpresa);
  return empresa ? { company: empresa.value, unit: unidad.value } : null;
}

/**
 * ¿Lo aplicado en Empresa / Unidad es EXACTAMENTE el filtro del foco? Con un foco de
 * empresa, la unidad tiene que ir vacía: con una unidad elegida la tabla ya no es "la
 * empresa del foco" y la miga no puede afirmarlo.
 */
export function tpvFocusFilterMatches(
  filter: TpvFocusFilter | null,
  company: unknown,
  unit: unknown,
): boolean {
  if (!filter) return false;
  const empresa = typeof company === 'string' ? company.trim() : '';
  const unidad = typeof unit === 'string' ? unit.trim() : '';
  return empresa === filter.company.trim() && unidad === (filter.unit ?? '').trim();
}
